import { ChevronLeft, Tag, Loader2, Check, X } from 'lucide-react';
import { Button } from '../ui/button';
import { useEffect, useRef, useState } from 'react';
import { useInitiatePaymentMutation, useGetPromoQuoteMutation } from '../../store/api/apiSlice';
import { CheckType } from '../../types';
import type { PromoQuoteResponse } from '../../types';

function formatNaira(value: number): string {
    return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(
        value || 0,
    );
}

const getReadableError = (err: any, fallback = 'An unexpected error occurred') => {
    const serverMessage = err?.data?.message ?? err?.message ?? err;

    if (Array.isArray(serverMessage)) {
        return serverMessage.join(', ');
    }

    if (typeof serverMessage === 'string') {
        return serverMessage;
    }

    if (serverMessage && typeof serverMessage === 'object') {
        return serverMessage.message || serverMessage.error || JSON.stringify(serverMessage);
    }

    return fallback;
};

interface PaymentModalProps {
    isOpen: boolean;
    onClose: () => void;
    onBack: () => void;
    checkType: CheckType;
    // One or the other: a single investigation, or a bulk batch paid for in one
    // charge. `subjectCount` is only for the copy — the server counts the batch
    // itself when pricing it, so the amount can't be talked down from here.
    investigationId?: string;
    batchId?: string;
    subjectCount?: number;
    // Plan features bought with the check. A short list means the employer
    // cherry picked, which the server charges at a markup.
    features?: string[];
    // Extras bought on top of the plan, at face value.
    addons?: string[];
    // Called when a FREE promo settles the investigation with no gateway charge.
    onFreeSuccess?: () => void;
}

export default function PaymentModal({
    isOpen,
    onBack,
    checkType,
    investigationId,
    batchId,
    subjectCount = 1,
    features = [],
    addons = [],
    onFreeSuccess,
}: PaymentModalProps) {
    const [initiatePayment, { isLoading: isInitiating, error: initiateError }] = useInitiatePaymentMutation();
    const [getPromoQuote, { isLoading: isQuoting }] = useGetPromoQuoteMutation();

    const [paymentLink, setPaymentLink] = useState<string>('');
    const [amountValue, setAmountValue] = useState<number>(0);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [isRedirecting, setIsRedirecting] = useState(false);

    // Promo state
    const [promoInput, setPromoInput] = useState('');
    const [appliedPromo, setAppliedPromo] = useState<PromoQuoteResponse | null>(null);
    const [promoError, setPromoError] = useState('');
    const [isPaying, setIsPaying] = useState(false);

    // Guard so the auto-initiate fires exactly once per open — without this, a
    // re-render (or React StrictMode in dev) can fire two Monnify inits back-to-back
    // and the gateway rejects the duplicate with a 400.
    const initiatedRef = useRef(false);

    useEffect(() => {
        if (!isOpen) {
            initiatedRef.current = false;
            return;
        }
        if ((!investigationId && !batchId) || paymentLink || initiatedRef.current) return;
        initiatedRef.current = true;

        // No query string here: Monnify appends its own "?paymentReference=..." to the
        // redirect URL. If we add "?payment=success" the gateway tacks on a *second* "?",
        // producing a malformed "...?payment=success?paymentReference=..." that 404s and
        // can't be parsed. The Investigations page detects the return via paymentReference.
        const redirectUrl = `${window.location.origin}/investigations`;

        initiatePayment({
            checkType,
            redirectUrl,
            ...(batchId ? { batchId } : investigationId ? { investigationId } : {}),
            ...(features.length > 0 && { features }),
            ...(addons.length > 0 && { addons }),
        })
            .unwrap()
            .then((response) => {
                if (response.paymentLink) setPaymentLink(response.paymentLink);
                if (response.amount) setAmountValue(response.amount);
            })
            .catch((err) => {
                console.error('[PaymentModal] Failed to initiate payment:', err);
                setErrorMessage(getReadableError(err, 'Failed to initiate payment.'));
            });
        // `addons` is a fresh array each render; key the effect on its contents
        // so a re-render can't re-fire the initiate.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isOpen,
        investigationId,
        batchId,
        checkType,
        initiatePayment,
        paymentLink,
        addons.join(','),
        features.join(','),
    ]);

    const handleApplyPromo = async () => {
        const code = promoInput.trim();
        if (!code) return;
        setPromoError('');
        try {
            const quote = await getPromoQuote({
                code,
                checkType,
                ...(addons.length > 0 && { addons }),
            }).unwrap();
            setAppliedPromo(quote);
        } catch (err) {
            setAppliedPromo(null);
            setPromoError(getReadableError(err, 'Invalid promo code.'));
        }
    };

    const handleRemovePromo = () => {
        setAppliedPromo(null);
        setPromoInput('');
        setPromoError('');
    };

    const handlePayClick = async () => {
        setErrorMessage('');

        // No promo → use the link prepared on open (existing behaviour).
        if (!appliedPromo) {
            if (!paymentLink) {
                setErrorMessage('Payment session not ready. Please try again.');
                return;
            }
            setIsRedirecting(true);
            window.location.href = paymentLink;
            return;
        }

        // Promo applied → re-initiate with the code so the gateway/amount reflect it.
        setIsPaying(true);
        try {
            const redirectUrl = `${window.location.origin}/investigations`;
            const response = await initiatePayment({
                checkType,
                redirectUrl,
                ...(batchId ? { batchId } : investigationId ? { investigationId } : {}),
                ...(features.length > 0 && { features }),
                ...(addons.length > 0 && { addons }),
                promoCode: appliedPromo.code,
            }).unwrap();

            // FREE code: no gateway. Investigation-first → already paid + employee invited.
            if (response.free) {
                setIsPaying(false);
                onFreeSuccess?.();
                return;
            }
            // Percentage/normal: go to the (discounted) checkout.
            if (response.paymentLink) {
                setIsRedirecting(true);
                window.location.href = response.paymentLink;
                return;
            }
            setIsPaying(false);
            setErrorMessage('Could not start checkout. Please try again.');
        } catch (err) {
            setIsPaying(false);
            setErrorMessage(getReadableError(err, 'Failed to apply the promo and start checkout.'));
        }
    };

    const handleRetryInitiate = () => {
        setErrorMessage('');
        setPaymentLink('');
        setAmountValue(0);
        initiatedRef.current = false;
    };

    if (!isOpen) return null;

    const packageName =
        checkType === CheckType.STANDARD ? 'Standard' : checkType === CheckType.ENHANCED ? 'Enhanced' : 'Pro';
    const effectiveAmount = appliedPromo ? appliedPromo.finalAmount : amountValue;
    const displayAmount = effectiveAmount ? formatNaira(effectiveAmount) : appliedPromo?.isFree ? 'Free' : '';
    const isFreePromo = appliedPromo?.isFree === true;

    return (
        <div className="fixed inset-0 z-50 flex items-end justify-end bg-black/50 md:items-center">
            <div className="h-full w-full overflow-y-auto bg-white shadow-2xl md:h-screen md:w-[600px] md:rounded-l-3xl">
                <div className="flex h-full flex-col p-6 md:p-8">
                    {/* Header */}
                    <div className="mb-6 flex items-center gap-4">
                        <button
                            onClick={onBack}
                            disabled={isInitiating || isRedirecting}
                            className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 transition-colors hover:bg-gray-200 disabled:opacity-50"
                        >
                            <ChevronLeft className="h-5 w-5 text-gray-600" />
                        </button>
                        <h2 className="text-2xl font-bold text-gray-900 md:text-3xl">Payment</h2>
                    </div>

                    {/* Description */}
                    <p className="mb-6 text-sm leading-relaxed text-gray-600">
                        Complete payment to finalize your investigation request. Once payment is confirmed, the employee
                        invitation will be sent immediately.
                    </p>

                    {isInitiating ? (
                        <div className="flex flex-1 items-center justify-center">
                            <div className="text-center">
                                <div className="mx-auto mb-4 h-16 w-16 animate-spin rounded-full border-4 border-gray-200 border-t-gray-900"></div>
                                <p className="text-gray-600">Preparing checkout...</p>
                            </div>
                        </div>
                    ) : initiateError && !paymentLink ? (
                        <div className="flex flex-1 items-center justify-center">
                            <div className="max-w-md text-center">
                                <p className="mb-4 text-red-600">
                                    {errorMessage || 'Failed to prepare checkout. Please try again.'}
                                </p>
                                <Button
                                    onClick={handleRetryInitiate}
                                    className="bg-gray-900 text-white hover:bg-gray-800"
                                >
                                    Retry
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-1 flex-col">
                            <div className="flex-1 overflow-y-auto">
                                {/* Package Type */}
                                <div className="mb-6 rounded-2xl border border-gray-100 bg-gray-50 p-6">
                                    <h3 className="text-xl font-bold text-gray-900">{packageName} Check</h3>
                                    {/* A bulk charge covers several people, so say so —
                                        otherwise the amount reads as a single check
                                        priced far too high. */}
                                    {batchId && subjectCount > 1 && (
                                        <p className="mt-1 text-sm text-gray-500">
                                            {subjectCount} people, paid for together
                                        </p>
                                    )}
                                    {appliedPromo && !appliedPromo.isFree && amountValue > effectiveAmount && (
                                        <p className="mt-2 text-sm font-medium text-gray-400 line-through">
                                            {formatNaira(amountValue)}
                                        </p>
                                    )}
                                    {displayAmount && (
                                        <p className="mt-1 text-2xl font-bold text-[#FF6B35]">{displayAmount}</p>
                                    )}
                                    {appliedPromo && (
                                        <p className="mt-1 text-xs font-medium text-green-600">
                                            {appliedPromo.isFree
                                                ? `Free with code ${appliedPromo.code}`
                                                : `${formatNaira(appliedPromo.discountAmount)} off with code ${appliedPromo.code}`}
                                        </p>
                                    )}
                                </div>

                                {/* Promo code */}
                                <div className="mb-6 rounded-2xl border border-gray-100 bg-white p-6">
                                    <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-900">
                                        <Tag className="h-4 w-4 text-[#FF6B35]" />
                                        Promo code
                                    </h4>
                                    {appliedPromo ? (
                                        <div className="flex items-center justify-between gap-3 rounded-lg border border-green-200 bg-green-50 px-3 py-2.5">
                                            <span className="flex items-center gap-2 text-sm font-medium text-green-700">
                                                <Check className="h-4 w-4" />
                                                {appliedPromo.code} applied
                                            </span>
                                            <button
                                                type="button"
                                                onClick={handleRemovePromo}
                                                className="text-green-700 hover:text-green-900"
                                                aria-label="Remove promo"
                                            >
                                                <X className="h-4 w-4" />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                value={promoInput}
                                                onChange={(e) => {
                                                    setPromoInput(e.target.value.toUpperCase());
                                                    setPromoError('');
                                                }}
                                                placeholder="Enter code"
                                                className="flex-1 rounded-lg border border-gray-200 px-3 py-2.5 text-sm uppercase focus:border-[#FF6B35] focus:outline-none"
                                            />
                                            <Button
                                                type="button"
                                                onClick={handleApplyPromo}
                                                disabled={!promoInput.trim() || isQuoting}
                                                className="rounded-lg bg-gray-900 px-4 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
                                            >
                                                {isQuoting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
                                            </Button>
                                        </div>
                                    )}
                                    {promoError && <p className="mt-2 text-xs text-red-600">{promoError}</p>}
                                </div>

                                {!isFreePromo && (
                                    <div className="mb-6 rounded-2xl border border-gray-100 bg-white p-6">
                                        <h4 className="mb-2 text-lg font-semibold text-gray-900">Secure Checkout</h4>
                                        <p className="text-sm text-gray-600">
                                            You&apos;ll be taken to Monnify&apos;s secure checkout page to complete
                                            payment with card, bank transfer, or USSD. After payment you&apos;ll be
                                            redirected back here.
                                        </p>
                                    </div>
                                )}

                                {errorMessage && (
                                    <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4">
                                        <p className="text-sm text-red-600">{errorMessage}</p>
                                    </div>
                                )}
                            </div>

                            {/* Action Buttons */}
                            <div className="space-y-3 border-t border-gray-100 pt-6">
                                <Button
                                    type="button"
                                    onClick={handlePayClick}
                                    disabled={(!appliedPromo && !paymentLink) || isRedirecting || isPaying}
                                    className="w-full rounded-xl bg-gray-900 py-6 text-base font-medium text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    {isRedirecting
                                        ? 'Redirecting...'
                                        : isPaying
                                          ? 'Processing...'
                                          : isFreePromo
                                            ? 'Complete for free'
                                            : 'Pay with Monnify'}
                                </Button>
                                <Button
                                    type="button"
                                    onClick={onBack}
                                    disabled={isRedirecting}
                                    className="w-full rounded-xl border border-gray-200 bg-white py-6 text-base font-medium text-gray-900 hover:bg-gray-50 disabled:opacity-50"
                                >
                                    Back to Package Selection
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
